// Exit-intent lead capture modal (desktop + mobile).
// Desktop: fires when the cursor leaves the viewport at the top edge (once per session).
// Mobile: fires on touch-friendly signals, deep scroll + long dwell, fast scroll-up
// from near the bottom, or a back-navigation attempt (once every 3 days).
// Not a chat widget, just a lead-capture prompt that opens LeadCaptureCTA.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import LeadCaptureCTA from "@/components/lp/LeadCaptureCTA";

const STORAGE_KEY_DESKTOP = "splora_exit_intent_shown_v1";
const STORAGE_KEY_MOBILE = "splora_exit_intent_mobile_ts_v1";
const MOBILE_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
const HIDDEN_PATHS = [/^\/admin/, /^\/seo-monitor/, /^\/checkout/];

const COPY: Record<string, { title: string; sub: string; cta: string; dismiss: string }> = {
  en: {
    title: "Before you go, need help planning?",
    sub: "Tell us where you'd like to go and we'll craft a free itinerary. Reply on WhatsApp within 2h.",
    cta: "Plan my trip",
    dismiss: "No thanks",
  },
  it: {
    title: "Prima di andare, ti aiutiamo a pianificare?",
    sub: "Dicci dove vorresti andare, creiamo un itinerario gratuito. Rispondiamo su WhatsApp entro 2h.",
    cta: "Pianifica il viaggio",
    dismiss: "No grazie",
  },
  de: {
    title: "Bevor Sie gehen, brauchen Sie Hilfe bei der Planung?",
    sub: "Sagen Sie uns, wohin es gehen soll, wir erstellen eine kostenlose Reiseroute. Antwort auf WhatsApp binnen 2 Std.",
    cta: "Reise planen",
    dismiss: "Nein danke",
  },
  fr: {
    title: "Avant de partir, besoin d'aide pour planifier ?",
    sub: "Dites-nous où vous voulez aller, nous créons un itinéraire gratuit. Réponse sur WhatsApp sous 2h.",
    cta: "Planifier mon voyage",
    dismiss: "Non merci",
  },
  es: {
    title: "Antes de irte, ¿te ayudamos a planificar?",
    sub: "Cuéntanos a dónde quieres ir y creamos un itinerario gratuito. Respuesta por WhatsApp en 2h.",
    cta: "Planificar mi viaje",
    dismiss: "No gracias",
  },
};

const ExitIntentModal = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const lang = (() => {
    const seg = location.pathname.split("/")[1];
    return (["de", "fr", "it", "es"].includes(seg) ? seg : "en") as keyof typeof COPY;
  })();
  const t = COPY[lang] ?? COPY.en;

  useEffect(() => {
    if (HIDDEN_PATHS.some((re) => re.test(location.pathname))) return;
    if (typeof window === "undefined") return;

    const isMobile = window.matchMedia("(max-width: 1024px)").matches;

    // ---------- DESKTOP: mouseleave at top edge ----------
    if (!isMobile) {
      try { if (sessionStorage.getItem(STORAGE_KEY_DESKTOP)) return; } catch { /* ignore */ }

      let armed = false;
      const armTimer = window.setTimeout(() => { armed = true; }, 10_000);

      const onLeave = (e: MouseEvent) => {
        if (!armed) return;
        if (e.clientY > 0) return;
        if (e.relatedTarget) return;
        try { sessionStorage.setItem(STORAGE_KEY_DESKTOP, "1"); } catch { /* ignore */ }
        setOpen(true);
        document.removeEventListener("mouseleave", onLeave);
      };

      document.addEventListener("mouseleave", onLeave);
      return () => {
        window.clearTimeout(armTimer);
        document.removeEventListener("mouseleave", onLeave);
      };
    }

    // ---------- MOBILE: touch-friendly signals ----------
    try {
      const last = Number(localStorage.getItem(STORAGE_KEY_MOBILE) || 0);
      if (last && Date.now() - last < MOBILE_COOLDOWN_MS) return;
    } catch { /* ignore */ }

    let armed = false;
    let triggered = false;
    let maxScrollPct = 0;
    let lastScrollY = window.scrollY;
    let lastScrollAt = Date.now();
    let idleTimer: number | null = null;

    const armTimer = window.setTimeout(() => { armed = true; }, 15_000); // 15s min dwell

    const trigger = () => {
      if (triggered || !armed) return;
      triggered = true;
      try { localStorage.setItem(STORAGE_KEY_MOBILE, String(Date.now())); } catch { /* ignore */ }
      setOpen(true);
      cleanup();
    };

    const resetIdle = () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        // Long idle after meaningful engagement → likely leaving
        if (maxScrollPct >= 55) trigger();
      }, 25_000);
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
      const y = window.scrollY;
      const pct = (y / scrollable) * 100;
      if (pct > maxScrollPct) maxScrollPct = pct;

      const now = Date.now();
      const dy = y - lastScrollY;
      const dt = Math.max(1, now - lastScrollAt);
      const velocity = dy / dt; // px per ms; negative = scrolling up

      // Fast upward flick after having explored the page
      if (armed && maxScrollPct >= 60 && velocity < -1.2 && y < scrollable * 0.55) {
        trigger();
      }

      lastScrollY = y;
      lastScrollAt = now;
      resetIdle();
    };

    const onVisibility = () => {
      // User backgrounds the tab after real engagement, soft exit signal
      if (document.visibilityState === "hidden" && armed && maxScrollPct >= 50) {
        trigger();
      }
    };

    // Back-navigation trap: push a state; if popstate fires, show modal instead of leaving
    let trappedHistory = false;
    const armHistoryTrap = () => {
      if (trappedHistory) return;
      trappedHistory = true;
      try { window.history.pushState({ splora_exit: true }, ""); } catch { /* ignore */ }
    };
    const onPopState = () => {
      if (armed && maxScrollPct >= 40) {
        trigger();
        // Re-push so a second back press actually leaves
        try { window.history.pushState({ splora_exit: true }, ""); } catch { /* ignore */ }
      }
    };
    const historyArmTimer = window.setTimeout(armHistoryTrap, 20_000);

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("popstate", onPopState);
    resetIdle();

    function cleanup() {
      window.clearTimeout(armTimer);
      window.clearTimeout(historyArmTimer);
      if (idleTimer) window.clearTimeout(idleTimer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("popstate", onPopState);
    }
    return cleanup;
  }, [location.pathname]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, zIndex: 9998,
        animation: "fadeIn 200ms ease-out",
      }}
    >
      <div
        style={{
          background: "#fff", borderRadius: 20, padding: 28,
          width: "100%", maxWidth: 440, boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
          color: "#111", position: "relative",
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => setOpen(false)}
          style={{
            position: "absolute", top: 12, right: 14, background: "transparent",
            border: 0, fontSize: 22, cursor: "pointer", color: "#888", lineHeight: 1,
          }}
        >
          ×
        </button>
        <div style={{ fontSize: 34, marginBottom: 8 }}>👋</div>
        <h3 id="exit-intent-title" style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700, lineHeight: 1.25 }}>
          {t.title}
        </h3>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: "#555", lineHeight: 1.5 }}>
          {t.sub}
        </p>

        <LeadCaptureCTA
          source="exit_intent_desktop"
          baseMessage="Hi Splorà! I was browsing your site and would love help planning a Cape Verde trip."
          uiLang={lang === "de" || lang === "fr" || lang === "es" || lang === "it" ? lang : "en"}
          className="lp-cta lp-cta--lg"
          style={{
            display: "block", width: "100%", padding: "14px 20px",
            background: "#25D366", color: "#fff", border: 0, borderRadius: 12,
            fontSize: 16, fontWeight: 700, cursor: "pointer",
          }}
        >
          {t.cta}
        </LeadCaptureCTA>

        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            marginTop: 10, width: "100%", padding: "10px", background: "transparent",
            border: 0, color: "#888", fontSize: 13, cursor: "pointer",
          }}
        >
          {t.dismiss}
        </button>
      </div>
    </div>
  );
};

export default ExitIntentModal;
